import {useState, useRef, useCallback, useMemo, useEffect} from 'react';
import ReactFlow, {
    ReactFlowProvider,
    addEdge,
    useNodesState,
    useEdgesState,
    Controls,
    Background, StraightEdge, StepEdge, SmoothStepEdge
} from 'react-flow-renderer';

import Rightbar from './Rightbar';
import Component from "./Node"
import './Workflow.scss';
import {UseRequest} from "../../hooks/UseRequest";
import {ApiEventSlug} from "../../config/Config";
import UseConfirm from "../../hooks/UseConfirm";
import toastr from "toastr";
import {processing} from "../../config/Status";


const Workflow = (props) => {
    const {journey, route, onSave, initialNodes, initialEdges} = props
    const reactFlowWrapper = useRef(null);

    const [availableNodes, setAvailableNodes] = useState([])
    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes || []);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges || []);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);

    const [showOptions, setShowOptions] = useState(false)
    const [currentNode, setCurrentNode] = useState(null)
    const [previousNode, setPreviousNode] = useState(null)
    const [processingClass, setProcessingClass] = useState('processing')


    const nodeTypes = useMemo(() => ({
        workflow_node: Component
    }), []);


    const edgeTypes = useMemo(() => ({
        default   : SmoothStepEdge,
        straight  : StraightEdge,
        step      : StepEdge,
        smoothstep: SmoothStepEdge
    }), []);


    const fetchNodes = async () => {
        const askForNodes = await UseRequest(
            `${process.env.REACT_APP_BASE_API}/components/get`, {
                request: {
                    method: 'PUT',
                    data  : {offset: 0, type: 'workflow_node', sortBy: 'order', sortOrder: 'ASC'}
                }
            }
        );


        const nodes = await (await askForNodes).json()


        return [{
            category  : 'all',
            label     : 'All Components',
            components: nodes.entities
        }]

    }


    useEffect(() => {
        fetchNodes().then((masterNodes) => {

            let hasEntry = false
            if (nodes && nodes.length && nodes.length > 0) {
                nodes.forEach((node) => {
                    if (node.data.is_entry) {
                        hasEntry = true
                    }
                })


                if (hasEntry) {
                    const newSections = getStatedComponents(masterNodes, true)
                    setAvailableNodes(newSections)
                } else {
                    setAvailableNodes(masterNodes)
                }
            } else {
                setAvailableNodes(masterNodes)
            }
        })


    }, [currentNode])


    const onConnect = useCallback((params) => {
        const HandlersWithLabels = ['yes', 'no'];
        if (HandlersWithLabels.includes(params.sourceHandle.toLocaleLowerCase())) {
            params.label = params.sourceHandle
        }

        setEdges((eds) => addEdge(params, eds))
    }, []);


    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);


    const getId = () => {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }


    const getStatedComponents = (sections, state) => {
        const newSections = []


        sections && sections.map((section) => {
            const newSection = {...section}
            const newComponents = []

            section.components && section.components.map((component) => {
                const newComponent = {...component}

                if (component.is_entry) {
                    newComponent.disabled = state;
                }

                newComponents.push(newComponent)
            })

            newSection.components = newComponents
            newSections.push(newSection)
        })

        return newSections
    }


    const onDrop = useCallback(
        (event) => {
            event.preventDefault();

            const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
            const component = JSON.parse(event.dataTransfer.getData('text/plain'));
            const type = component.type

            // check if the dropped element is valid
            if (typeof type === 'undefined' || !type) {
                return;
            }

            const position = reactFlowInstance.project({
                x: event.clientX - reactFlowBounds.left,
                y: event.clientY - reactFlowBounds.top,
            });


            if (component.is_entry) {
                const newSections = getStatedComponents(availableNodes, true)
                setAvailableNodes(newSections)
            }

            const newNode = {
                id  : getId(),
                type,
                position,
                data: {
                    slug         : component.slug,
                    icon         : component.icon,
                    label        : component.label,
                    is_entry     : component.is_entry || false,
                    is_dispatcher: component.is_dispatcher || false,
                    id_provider  : component.is_provider || false
                },
            };

            setNodes((nds) => nds.concat(newNode));
        },
        [reactFlowInstance, availableNodes]
    );


    const handleNodeClick = (event, node) => {

        let prevNode = {};
        if (edges?.length > 0) {
            edges.map(edge => {
                if (edge.target === node.id) {
                    prevNode.id = edge.source;
                }
            })
        }

        if (prevNode?.id) {
            nodes.map(node => {
                if (node.id === prevNode.id) {
                    prevNode = node;
                }
            })
        }


        setCurrentNode(node)
        setPreviousNode(prevNode);
        setShowOptions(!showOptions)
    }


    const handlePaneClick = (event) => {
        setShowOptions(false)
    }


    const handleNodeDelete = (deletedNodes) => {
        let isInput = false
        let ApiEvent = null

        deletedNodes && deletedNodes.map((node) => {
            if (node.data.is_entry) {
                isInput = true
            }

            if (node.data.slug === ApiEventSlug) {
                ApiEvent = node
            }
        })

        if (isInput) {
            const newSections = getStatedComponents(availableNodes, false)
            setAvailableNodes(newSections)
        }


        if (ApiEvent != null) {
            UseConfirm({
                title      : 'Be careful!',
                description: 'By confirming the deletion of this entry point, the endpoint will be disabled and the journey will be updated automatically',
                onConfirm  : async () => {
                    UseRequest(`${process.env.REACT_APP_BASE_API}/endpoints/delete`, {
                        request: {
                            method: 'DELETE',
                            data  : {
                                endpoint_key: ApiEvent.data.form.tabs.settings.fields.endpoint_key.value
                            }

                        }
                    }).then(async (response) => {
                        if (response.status < 200 && response.status > 299) {

                            const data = await response.json()
                            if (data && data.message) {
                                toastr['error'](data.message, data.title, {
                                    timeOut              : 1500,
                                    preventDuplicates    : true,
                                    preventOpenDuplicates: true
                                });
                            }

                            const newNodes = []
                            nodes.map((node) => {
                                if (node.id != ApiEvent.id) {
                                    newNodes.push(node)
                                }
                            })
                            await handleSave(newNodes)
                        }
                    });
                },
                onCancel   : () => {
                    setNodes(nodes)
                }
            });
        }

        setShowOptions(false)
    }


    const handleSave = async (nodesOrEvent) => {
        const newNodes = isEvent(nodesOrEvent) ? nodes : nodesOrEvent
        onSave({nodes: newNodes, edges: edges})


        if (currentNode) {
            setShowOptions(false)
        }
    }


    const isEvent = (interaction) => {
        return typeof interaction == "object" &&
            interaction.nativeEvent instanceof Event
    }


    const FlowOptions = {
        nodes             : nodes,
        edges             : edges,
        onDrop            : onDrop,
        snapGrid          : [8, 8],
        snapToGrid        : true,
        onConnect         : onConnect,
        onDragOver        : onDragOver,
        deleteKeyCode     : 'Delete',
        onNodesChange     : onNodesChange,
        onEdgesChange     : onEdgesChange,
        onInit            : setReactFlowInstance,
        onNodeClick       : handleNodeClick,
        onPaneClick       : handlePaneClick,
        onNodesDelete     : handleNodeDelete,
        connectionLineType: 'smoothstep',
        edgeTypes         : edgeTypes,
        nodeTypes         : nodeTypes
    }


    const RightbarOptions = {
        journey       : journey,
        onClickSave   : handleSave,
        showOptions   : showOptions,
        currentNode   : currentNode,
        previousNode  : previousNode,
        availableNodes: availableNodes,
    }


    const HeaderProps = {
        route: route
    }

    const hideProcessing = () => {
        setProcessingClass('processing d-none')
    }

    return (
        <div className="dndflow">
            <ReactFlowProvider>
                <div className="reactflow-wrapper" ref={reactFlowWrapper}>
                    {journey.status == processing && <div className={processingClass}>
                        <div>
                            <span className="title">read-only is active</span>
                            <span className="subtitle">The current journey is being processed. Until the process is finished, the possibility of saving this entity was disabled</span>
                            <a onClick={hideProcessing} className="btn-understand btn btn-primary btn-sm">
                                I understand
                            </a>
                        </div>
                    </div>}
                    <ReactFlow {...FlowOptions}>
                        <Background color="#888" gap={8}/>
                        <Controls/>
                    </ReactFlow>
                </div>
                <Rightbar {...RightbarOptions}/>
            </ReactFlowProvider>
        </div>
    );
};

export default Workflow;
